import { Empty, Spin, Tooltip } from 'antd'
import Control from 'components/Control/Control'
import AntdInput from 'components/Form/Input'
import AntdSelect from 'components/Form/Select'
import Label from 'components/Label/Label'
import { ExclamationCircleFilled } from '@ant-design/icons'
import styled from 'styled-components'
import { FormItem } from 'modules/Onboarding/Register/Education/style'
import {
  Heading,
  SpinWrapper,
} from 'modules/Onboarding/Register/PersonalInfo/style'
import React, { Fragment, useCallback, useMemo, useRef, useEffect } from 'react'
import { debounce } from '../../../../utils/helpers'
import { RedNote } from './style'

const PendingIcon = styled(ExclamationCircleFilled)`
  color: #eb5d60;
  font-size: 14px;
  margin-left: 6px;
  position: relative;
  top: -5px;
`

const LabelWithPending = ({ children, isPending }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <Label>{children}</Label>
    {isPending && (
      <Tooltip title="Data Verification Pending">
        <PendingIcon />
      </Tooltip>
    )}
  </div>
)

const Address = ({
  form,
  loadingCountry,
  loadingState,
  loadingCity,
  setLoadingCountry,
  setLoadingState,
  setLoadingCity,
  setIsUpdateButtonEnabled,
  countryList,
  stateList,
  cityList,
  onCountryChange,
  onStateChange,
  onCityChange,
  heading,
  address1Name,
  address2Name,
  countryName,
  stateName,
  cityName,
  pincode,
  isTemAddress,
  isSameAddress,
  countryValue,
  stateValue,
  frozenFields = {},
  cityValue,
  countryId,
  stateId,
  getStateData,
  getCityData,
  secondAddress1Name,
  secondAddress2Name,
  secondPincode,
  cityId,
  isCorporateMasterExist,
  errorNote,
  setErrorNote,
  getListOfCountries,
  setStateName,
  setStateId,
  setCountryName,
  setCountryId,
  setCityName,
  setCityId,
  handlePinCode,
  pendingFields = {},
}) => {
  // Capture initial pincode value to determine if field should be frozen
  const initialPincodeRef = useRef(null)
  
  useEffect(() => {
    if (initialPincodeRef.current === null) {
      const initialValue = form.getFieldValue(pincode)
      initialPincodeRef.current = initialValue || ''
    }
  }, [])
  
  const delayStateToDb = useCallback(
    debounce((data1, data2, data3) => {
      form.setFieldsValue({
        [stateName]: '',
        [stateName + 'Id']: '',
      })
      setStateName('')
      setStateId('')
      getStateData(data1, data2, data3)
      setErrorNote(prevError => ({
        ...prevError,
        [stateName]: '',
        [cityName]: '',
      }))
    }, 500),
    []
  )

  const delayCitiesToDb = useCallback(
    debounce((data1, data2, data3, data4) => {
      form.setFieldsValue({
        [cityName]: '',
        [cityName + 'Id']: '',
      })
      setCityId('')
      setCityName('')
      getCityData(data1, data2, data3, data4)
      setErrorNote(prevError => ({
        ...prevError,
        [cityName]: '',
      }))
    }, 500),
    []
  )
  const delayCountryToDb = useCallback(
    debounce(data => {
      form.setFieldsValue({
        [countryName]: '',
        [countryName + 'Id']: '',
      })
      setCountryName('')
      setCountryId('')
      getListOfCountries(data, false)

      setErrorNote(prevError => ({
        ...prevError,
        [countryName]: '',
        [stateName]: '',
        [cityName]: '',
      }))
    }, 500),
    []
  )

  const handleInputChange = e => {
    setIsUpdateButtonEnabled(false)
  }

  return (
    <Fragment>
      {heading && (
        <Control top="35px">
          <Heading>{heading}</Heading>
        </Control>
      )}
      <Control top="25px" flexStart column>
        <LabelWithPending isPending={pendingFields[address1Name] !== undefined}>Address line 1</LabelWithPending>
        <AntdInput
          placeholder="Enter your address"
          name={address1Name}
          maxLength={120}
          disabled={(frozenFields[address1Name] && !pendingFields[address1Name]) || (isTemAddress && isSameAddress)}
          rules={[
            {
              min: 3,
              max: 120,
              message:
                'Please enter address 1! Minimum 3 characters , Maximum 120 characters',
            },
          ]}
          onBlur={e =>
            isSameAddress &&
            form.setFieldValue(secondAddress1Name, e.target.value)
          }
          onChange={handleInputChange}
        />
      </Control>

      <Control top="25px" flexStart column>
        <LabelWithPending isPending={pendingFields[address2Name] !== undefined}>Address line 2</LabelWithPending>
        <AntdInput
          placeholder="Enter your address"
          name={address2Name}
          maxLength={120}
          disabled={(frozenFields[address2Name] && !pendingFields[address2Name]) || (isTemAddress && isSameAddress)}
          rules={[
            {
              min: 3,
              max: 120,
              message:
                'Please enter address 2! Minimum 3 characters , Maximum 120 characters',
            },
          ]}
          onBlur={e =>
            isSameAddress &&
            form.setFieldValue(secondAddress2Name, e.target.value)
          }
          onChange={handleInputChange}
        />
      </Control>

      <Control top="25px" flexStart gap="15px">
        <Control flexStart column>
          <LabelWithPending isPending={pendingFields[countryName] !== undefined || pendingFields[countryName + 'Id'] !== undefined}>Country *</LabelWithPending>
          <FormItem name={countryName}>
            <div></div>
          </FormItem>
          <AntdSelect
            placeholder="Select"
            name={countryName + 'Id'}
            filterOption={false}
            disabled={(() => {
              const hasValue = form.getFieldValue(countryName + 'Id') && form.getFieldValue(countryName + 'Id') !== ''
              const isFrozen = hasValue && (frozenFields[countryName] && !pendingFields[countryName]) && (frozenFields[countryName + 'Id'] && !pendingFields[countryName + 'Id'])
              const isSameAddr = isTemAddress && isSameAddress
              
              return isFrozen || isSameAddr
            })()}
            rules={[
              {
                required: true,
                message: 'Please select country!',
              },
              {
                validator: async (_, value) => {
                  if (value) {
                    let masterList = await isCorporateMasterExist(
                      value,
                      countryName
                    )

                    if (masterList) {
                      return Promise.reject(new Error('Please select country!'))
                    } else {
                      return Promise.resolve()
                    }
                  }
                  return Promise.resolve()
                },
              },
            ]}
            OptionData={loadingCountry ? [] : countryList}
            notFoundContent={
              loadingCountry ? (
                <SpinWrapper center>
                  <Spin />
                </SpinWrapper>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )
            }
            valueParam="name"
            nameParam="name"
            onChange={onCountryChange}
            value={countryValue}
            showSearch={true}
            onSearch={data => {
              setLoadingCountry(true)
              delayCountryToDb(data)
            }}
          />
          {errorNote?.[countryName] && (
            <RedNote>{errorNote?.[countryName]}</RedNote>
          )}
        </Control>
        <Control flexStart column>
          <LabelWithPending isPending={pendingFields[stateName] !== undefined || pendingFields[stateName + 'Id'] !== undefined}>State *</LabelWithPending>
          <FormItem name={stateName}>
            <div></div>
          </FormItem>
          <AntdSelect
            showSearch={true}
            filterOption={false}
            placeholder="Select"
            name={stateName + 'Id'}
            disabled={(() => {
              const hasValue = form.getFieldValue(stateName + 'Id') && form.getFieldValue(stateName + 'Id') !== ''
              const isFrozen = hasValue && (frozenFields[stateName] && !pendingFields[stateName]) && (frozenFields[stateName + 'Id'] && !pendingFields[stateName + 'Id'])
              const isSameAddr = isTemAddress && isSameAddress 
              return isFrozen || isSameAddr
            })()}
            rules={[
              {
                required: true,
                message: 'Please select state!',
              },
              {
                validator: async (_, value) => {
                  if (value) {
                    try {
                      let masterList = await isCorporateMasterExist(
                        value,
                        stateName
                      )

                      if (masterList) {
                        return Promise.reject(new Error('Please select state!'))
                      } else {
                        return Promise.resolve()
                      }
                    } catch (error) {
                      console.error('Error validating state:', error)
                      return Promise.resolve()
                    }
                  }
                  return Promise.resolve()
                },
              },
            ]}
            OptionData={loadingState ? [] : stateList}
            notFoundContent={
              loadingState ? (
                <SpinWrapper center>
                  <Spin />
                </SpinWrapper>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )
            }
            valueParam="name"
            nameParam="name"
            onChange={onStateChange}
            value={stateValue}
            onSearch={data => {
              setLoadingState(true)
              delayStateToDb(countryId, !isTemAddress, data)
            }}
          />
          {errorNote?.[stateName] && (
            <RedNote>{errorNote?.[stateName]}</RedNote>
          )}
        </Control>
      </Control>

      <Control top="25px" flexStart gap="15px">
        <Control flexStart column>
          <LabelWithPending isPending={pendingFields[cityName] !== undefined || pendingFields[cityName + 'Id'] !== undefined}>City *</LabelWithPending>
          <FormItem name={cityName}>
            <div></div>
          </FormItem>
          <AntdSelect
            placeholder="Select"
            name={cityName + 'Id'}
            filterOption={false}
            disabled={(() => {
              const hasValue = form.getFieldValue(cityName + 'Id') && form.getFieldValue(cityName + 'Id') !== ''
              const isFrozen = hasValue && (frozenFields[cityName] && !pendingFields[cityName]) && (frozenFields[cityName + 'Id'] && !pendingFields[cityName + 'Id'])
              const isSameAddr = isTemAddress && isSameAddress
              return isFrozen || isSameAddr
            })()}
            rules={[
              {
                required: true,
                message: 'Please select city!',
              },

              {
                validator: async (_, value) => {
                  if (value) {
                    let masterList = await isCorporateMasterExist(
                      value,
                      cityName
                    )

                    if (masterList) {
                      return Promise.reject(new Error('Please select city!'))
                    } else {
                      return Promise.resolve()
                    }
                  }
                  return Promise.resolve()
                },
              },
            ]}
            OptionData={loadingCity ? [] : cityList}
            notFoundContent={
              loadingCity ? (
                <SpinWrapper center>
                  <Spin />
                </SpinWrapper>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )
            }
            valueParam="name"
            nameParam="name"
            onChange={onCityChange}
            value={cityValue}
            showSearch={true}
            onSearch={data => {
              setLoadingCity(true)
              delayCitiesToDb(countryId, stateId, !isTemAddress, data)
            }}
          />
          {errorNote?.[cityName] && <RedNote>{errorNote?.[cityName]}</RedNote>}
        </Control>

        <Control flexStart column>
          <LabelWithPending isPending={pendingFields[pincode] !== undefined}>Pincode *</LabelWithPending>
          <AntdInput
            id={pincode}
            placeholder="121212"
            name={ pincode}
            type={'number'}
            disabled={(() => {
              const initialValue = initialPincodeRef.current
              const hasValue = initialValue && initialValue !== 0 && initialValue !== ''
              const isFrozen = hasValue && (frozenFields[pincode] && !pendingFields[pincode])
              const isSameAddr = isTemAddress && isSameAddress
              return isFrozen || isSameAddr
            })()}
            rules={[
              {
                validator: (_, value) => {
                  if (value >= 100000 && value <= 999999) {
                    return Promise.resolve()
                  } else {
                    return Promise.reject(
                      new Error('Please Enter Valid Pincode')
                    )
                  }
                },
              },
            ]}
            value={form.getFieldValue(pincode)}
            onChange={e => {
              handlePinCode(e)

              handleInputChange(e)
            }}
          />
        </Control>
      </Control>
    </Fragment>
  )
}

export default Address
